// Original / Perfectly Imperfect cards on the product page (snippets/product-condition-picker.liquid).
// When nothing is picked, add to cart is blocked until a card is chosen. The picked card's price,
// % off, Afterpay amount and returns line are pushed into the rest of the product section through
// [data-condition-*] hooks.
class ProductConditionPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest("#main-product-section") || document;
    this.cartForm = this.closest("cart-form");
    this.cards = Array.from(this.querySelectorAll("[data-condition-card]"));
    this.error = this.querySelector("[data-condition-error]");
    this.explainer = this.querySelector("[data-condition-explainer]");
    this.toggleButton = this.querySelector("[data-condition-toggle]");
    this.closeButton = this.querySelector("[data-condition-close]");
    this.selected = this.cards.find((card) => card.getAttribute("aria-checked") === "true") || null;

    this.cards.forEach((card) => card.addEventListener("click", () => this.select(card)));
    this.toggleButton?.addEventListener("click", () => this.setExplainer(this.explainer.hidden));
    this.closeButton?.addEventListener("click", () => this.setExplainer(false));
    this.querySelector("[data-condition-video-play]")?.addEventListener("click", () => this.playVideo());
    this.querySelector("[data-condition-video-shrink]")?.addEventListener("click", () => this.shrinkVideo());
    // Back to the thumbnail once it has played through
    if (this.querySelector("[data-condition-video-play]")) {
      this.querySelector("[data-condition-video] video")?.addEventListener("ended", () => this.shrinkVideo());
    }

    // Capture phase on the form runs before cart-form's own click handler on the button.
    // Upsell cards have their own nested cart-form, so their buttons are left alone.
    this.guardAddToCart = (event) => {
      const button = event.target.closest('button[type="addToCart"]');
      if (!button || button.closest("cart-form") !== this.cartForm || this.selected) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    };
    this.cartForm?.addEventListener("click", this.guardAddToCart, true);

    // Only one condition in stock: it was picked server-side, so make sure the form holds it
    if (this.selected) {
      customElements.whenDefined("product-card").then(() => this.syncProductCard());
      this.setExplainer(this.selected.dataset.pi === "true");
    }
    this.render();
  }

  disconnectedCallback() {
    this.cartForm?.removeEventListener("click", this.guardAddToCart, true);
  }

  select(card) {
    if (card === this.selected) return;
    this.selected = card;
    delete this.dataset.error;
    if (this.error) this.error.hidden = true;
    customElements.whenDefined("product-card").then(() => this.syncProductCard());
    this.setExplainer(card.dataset.pi === "true");
    this.render();
  }

  syncProductCard() {
    const productCard = this.closest("product-card");
    const variantId = Number(this.selected.dataset.variantId);
    if (!productCard?.dataStore || productCard.data.selected_variant_id?.value === variantId) return;
    productCard.dataStore.updateMany({ selected_variant_id: variantId });
  }

  showError() {
    this.dataset.error = "true";
    if (this.error) this.error.hidden = false;
    this.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  // Opens by itself when Perfectly Imperfect is picked; the shopper can always close it
  setExplainer(open) {
    if (!this.explainer) return;
    this.explainer.hidden = !open;
    this.toggleButton?.setAttribute("aria-expanded", String(open));
    if (!open) return this.shrinkVideo();
    // The "Full width, plays when opened" video setting
    if (this.querySelector("[data-condition-video][data-autoplay]")) this.autoplayVideo();
  }


  autoplayVideo() {
    const video = this.querySelector("[data-condition-video] video");
    if (!video) return;
    if (this.videoAutoMuted) {
      video.muted = false;
      video.currentTime = 0;
    }
    video.play().then(() => {
      if (!video.muted) this.videoAutoMuted = false;
    }).catch((error) => {
      if (error.name !== "NotAllowedError" || video.muted) return;
      video.muted = true;
      this.videoAutoMuted = true;
      video.play().catch(() => {});
    });
  }

  // The explainer video starts as a thumbnail beside the text; tapping it grows it to full width and plays it
  playVideo() {
    const wrapper = this.querySelector("[data-condition-video]");
    const video = wrapper?.querySelector("video");
    if (!video) return;
    wrapper.dataset.expanded = "true";
    video.controls = true;
    video.play().catch(() => {});
  }

  shrinkVideo() {
    const wrapper = this.querySelector("[data-condition-video]");
    const video = wrapper?.querySelector("video");
    if (!video) return;
    video.pause();
    // The "Full width" layout setting has no thumbnail to go back to
    if (!this.querySelector("[data-condition-video-play]")) return;
    wrapper.dataset.expanded = "false";
    video.controls = false;
  }

  render() {
    const card = this.selected;
    // With nothing picked the picker itself holds the "from" price and "Up to X% off"
    const state = (card || this).dataset;

    this.cards.forEach((c) => c.setAttribute("aria-checked", String(c === card)));
    this.setText("[data-condition-price]", state.price);
    this.setText("[data-condition-compare]", state.compare);
    this.setText("[data-condition-pill]", state.pill);
    this.setText("[data-condition-returns]", state.returns);
    this.section.querySelectorAll("[data-condition-atc]").forEach((button) => {
      button.dataset.ready = String(Boolean(card));
      const label = button.querySelector("[data-condition-atc-label]");
      if (label) label.textContent = card ? `${button.dataset.label} - ${card.dataset.price}` : button.dataset.selectLabel;
    });

    // Afterpay's <square-placement> re-renders when its amount changes
    const amount = (Number(state.priceCents) / 100).toFixed(2);
    this.section.querySelectorAll("square-placement").forEach((el) => el.setAttribute("data-amount", amount));
  }

  setText(selector, text = "") {
    this.section.querySelectorAll(selector).forEach((el) => {
      el.textContent = text;
      el.hidden = !text;
    });
  }
}

if (!customElements.get("product-condition-picker")) {
  customElements.define("product-condition-picker", ProductConditionPicker);
}
